import type { ReactNode } from 'react';
import cn from 'classnames';
import { IAskAiChatMessage, MessageStatus, TurnStatus } from '@/creditflowresearch/domain/vo/AskAiChat';
import { getProductAtLine, getProductSections } from '@/creditflowresearch/application/AskAiChat/helpers/productSections';
import MarkdownRenderer from '../MarkdownRenderer';
import InlineExportButton from '../InlineExportButton';
import ProcessingIndicator from '../ProcessingIndicator';
import ProductTableControls from '../ProductTableControls';
import { isProcessingVisible } from '../utils';
import MessageActions from './MessageActions';
import MessageError from './MessageError';
import StoppedNote from './StoppedNote';
import styles from '../askAiChat.module.scss';

interface IAssistantMessageProps {
  message: IAskAiChatMessage,
  turnStatus?: TurnStatus,
  onRetry: (messageId: string) => void,
  onEdit: (messageId: string) => void,
}

const AssistantMessage = ({ message, turnStatus, onRetry, onEdit }: IAssistantMessageProps) => {
  const isFailed = message.status === MessageStatus.Error || turnStatus === TurnStatus.Failed;
  const isComplete = !isFailed && message.status === MessageStatus.Completed;
  const [exportProduct] = isComplete ? getProductSections(message.content) : [];

  const renderTableActions = (tableLine: number): ReactNode => {
    const product = getProductAtLine(message.content, tableLine);

    return product && <ProductTableControls messageId={ message.id } product={ product } />;
  };

  return (
    <div
      className={ cn(styles.assistantMessage, { [styles.assistantMessageFailed]: isFailed }) }
      data-test="askAiChatAssistantMessage"
    >
      { !!message.content && (
        <MarkdownRenderer
          content={ message.content }
          exportButton={ exportProduct && <InlineExportButton messageId={ message.id } product={ exportProduct } /> }
          renderTableActions={ isComplete ? renderTableActions : undefined }
        />
      ) }
      { isProcessingVisible(message) && (
        <ProcessingIndicator label={ message.stageLabel } progress={ message.progress } />
      ) }
      { turnStatus === TurnStatus.Cancelled && <StoppedNote /> }
      { isComplete && !!message.content && (
        <MessageActions content={ message.content } onEdit={ () => onEdit(message.id) } />
      ) }
      { isFailed && <MessageError text={ message.errorMessage } onRetry={ () => onRetry(message.id) } /> }
    </div>
  );
};

export default AssistantMessage;
